import styles from '../StatisticPage.module.scss';

import { LinearProgress } from '@mui/material';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { StatisticSalesVolumeBySupplierTable } from '@work4all/components/lib/dataDisplay/basic-table/reporting/StatisticSalesVolumeBySupplierTable';

import { useDataProvider } from '@work4all/data';

import { StatisticSalesVolumeBySupplier } from '@work4all/models/lib/Classes/StatisticSalesVolumeBySupplier.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { StatisticTimeRange } from '@work4all/models/lib/Enums/StatisticTimeRange.enum';

import { settings, useSetting } from '../../../settings';
import { useTableBorders } from '../../../utils/use-table-borders/use-table-borders';
import { useOldestInvoice } from '../util/use-oldest-invoice';

import { useStatisticNavigation } from './hooks/use-statistics-navigation';
import { StatisticDateWheelPickerField } from './statistic-date-wheel-picker-field/StatisticDateWheelPickerField';

export function StatisticSalesVolumeBySupplierContainer() {
  const statisticConsiderInvoiceLedger = useSetting(
    settings.statisticConsiderInvoiceLedger()
  );

  const oldestInvoice = useOldestInvoice();

  const { t } = useTranslation();

  const { currentYear, maxYear, setCurrentYear } = useStatisticNavigation();

  const requestData: DataRequest = useMemo(() => {
    const currentYearToDateTime = DateTime.fromJSDate(currentYear);
    const startDate = currentYearToDateTime;
    const endDate = currentYearToDateTime.plus({ month: 11 }).endOf('month');

    const data: StatisticSalesVolumeBySupplier = {
      start: null,
      end: null,
      timeSpanUnit: null,
      columnCount: null,
      columns: [
        {
          rangeStart: null,
          rangeEnd: null,
          name: null,
          rowCount: null,
          rows: [
            {
              date: null,
              objCode: null,
              amount: null,
              lieferant: {
                number: null,
                name: null,
                id: null,
              },
            },
          ],
        },
      ],
    };

    return {
      entity: Entities.statisticSalesVolumeBySupplier,
      data: data,
      vars: {
        rangeType: StatisticTimeRange.MONTH,
        rangeStart: startDate.toFormat('yyyy-MM-dd'),
        rangeEnd: endDate.toFormat('yyyy-MM-dd'),
        onlyRaInvoices: statisticConsiderInvoiceLedger.value,
        useDifferentBillingAddressIfUsed: false,
      },
      completeDataResponse: true,
    };
  }, [currentYear, statisticConsiderInvoiceLedger.value]);

  const data = useDataProvider(requestData);

  const isLoading = data.loading || data.pending;

  const tableBorders = useTableBorders();

  return (
    <>
      {isLoading && (
        <LinearProgress
          style={{ position: 'absolute', width: '100%', zIndex: 200 }}
        />
      )}
      <div className={styles.controlBar}>
        <div style={{ width: '15rem', maxWidth: '50%' }}>
          <StatisticDateWheelPickerField
            min={
              oldestInvoice?.data?.[0]?.date
                ? new Date(oldestInvoice?.data?.[0]?.date)
                : new Date()
            }
            max={maxYear}
            unit="year"
            label={t('COMMON.BUSINESS_YEAR')}
            value={currentYear}
            onChange={(value) => {
              setCurrentYear(value);
            }}
          />
        </div>
      </div>
      <div className={styles.tableWrapper}>
        <StatisticSalesVolumeBySupplierTable
          tableClasses={tableBorders}
          data={data.data as StatisticSalesVolumeBySupplier}
        />
      </div>
    </>
  );
}
